import * as React from 'react'
import { FC, useCallback, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { AgreementList } from './AgreementList/AgreementList'
import { FM_FORM_STATUSES } from '@user/store/agreements/typings'
import {
  getAgreements,
  getAgreementWidgetSettingsData,
} from '@user/store/dashboard/widgets/manageAgreements/api'
import {
  selectAllAgreements,
  selectManageAgreements,
  setPage,
} from '@user/store/dashboard/widgets/manageAgreements/manageAgreements'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import CircularProgress from '@mui/material/CircularProgress'
import FormControl from '@mui/material/FormControl'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'

import {
  IUserPreferenceGeneralListItemType,
  saveGeneralPreferences,
  userGeneralPreferencesList,
} from '@user/store/userPreferences/userPreferencesGeneral'

const styles = {
  formControl: {
    display: 'flex',
  },
}

export const ContentFront: FC = () => {
  const { isLoading, page } = useSelector(selectManageAgreements)
  const agreements = useSelector(selectAllAgreements)
  const dispatch = useAppDispatch()
  const userGeneralPreferences = useSelector(userGeneralPreferencesList)

  const filterValue = useMemo(
    () =>
      userGeneralPreferences?.find(
        (row) =>
          row?.type ==
          IUserPreferenceGeneralListItemType.ManageAgreementWidgetFilter,
      )?.value ?? FM_FORM_STATUSES.All,
    [userGeneralPreferences],
  )

  useEffect(() => {
    dispatch(
      getAgreements({
        page,
        query: filterValue == 'ALL' ? '' : filterValue,
      }),
    )
  }, [dispatch, page, filterValue])

  useEffect(() => {
    dispatch(getAgreementWidgetSettingsData())
  }, [dispatch])

  const handleSetStatus = useCallback(
    async (selectedStatus: string) => {
      await dispatch(
        saveGeneralPreferences({
          type: IUserPreferenceGeneralListItemType.ManageAgreementWidgetFilter,
          value: selectedStatus,
        }),
      )
      dispatch(setPage(1))
    },
    [dispatch],
  )

  return (
    <>
      {isLoading && (
        <Box display="flex" justifyContent="center" alignItems="center">
          <CircularProgress />
        </Box>
      )}
      {!isLoading && !!agreements && (
        <>
          <Box mb={1.2}>
            <FormControl sx={styles.formControl}>
              <Select
                variant="standard"
                size="medium"
                data-test-id="manage-agreements-filter-select"
                onChange={(e) => {
                  handleSetStatus(e.target.value)
                }}
                value={filterValue}
              >
                <MenuItem value={FM_FORM_STATUSES.All}>
                  Show All Active Agreements
                </MenuItem>
                <MenuItem value={FM_FORM_STATUSES.Draft}> Draft </MenuItem>
                <MenuItem value={FM_FORM_STATUSES.InProgress}>
                  In Progress
                </MenuItem>
                <MenuItem value={FM_FORM_STATUSES.OutForSignature}>
                  Out for Signature
                </MenuItem>
                <MenuItem value={FM_FORM_STATUSES.Signed}>Signed</MenuItem>
                <MenuItem value={FM_FORM_STATUSES.Complete}>Complete</MenuItem>
                <MenuItem value={FM_FORM_STATUSES.Canceled}>Canceled</MenuItem>
                <MenuItem value={FM_FORM_STATUSES.Declined}>Declined</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {!agreements.length && (
            <Alert severity="info">
              There are no agreements that match your current search criteria.
            </Alert>
          )}

          {agreements.length > 0 && <AgreementList agreements={agreements} />}
        </>
      )}
      {!isLoading && !agreements && (
        <Alert severity="error">
          An error occured while loading agreements.
        </Alert>
      )}
    </>
  )
}

ContentFront.displayName = 'ManageAgreementsContentFront'
